/**
 * The headline — the one sentence under a screen's title that says what the data SAYS.
 * Spec v6b §3, audit v6 rule 1, `docs/adr/9013`.
 *
 * "336 options hold half the residual stock" rather than "Residual distribution". It is a
 * CONCLUSION, so it depends on the data, and the dependence is where a free-text sentence would
 * go wrong: a positive total over a mixed set of transfers, a "none" that is really "not yet".
 * So it is never free text. It is:
 *
 *   * a closed set of VARIANTS, each a catalogue key in all three locales;
 *   * a RULE — a pure function — that picks the variant from the data and computes its
 *     arguments;
 *   * a test per rule, over the cases that decide between variants (`./headline-testing.ts`).
 *
 * Every figure in the sentence is computed from the read (leader ruling 1). A money figure is
 * compacted with `formatCompactNumber` in the rule, because the rule is where the locale is
 * known and the catalogue places the result; a count is passed raw so ICU can pluralise it.
 *
 * The rule for a screen lives with the screen — `features/<chapter>/headline.ts` — and this
 * module is only the shape every such rule has.
 */

import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import type { ICUValues } from '../primitives';

/** What a rule answers for one read: which variant, and the arguments its sentence takes. */
export interface HeadlineChoice<V extends string> {
  readonly variant: V;
  readonly values?: ICUValues;
}

/** What a rule is handed besides the data. */
export interface HeadlineContext {
  /** The planner's locale, for the figures the rule formats (compact money, percentages). */
  readonly locale: string;
}

export interface HeadlineRule<D, V extends string> {
  /** One catalogue key per variant. Every variant has one, so the sentence is never absent. */
  readonly keys: Readonly<Record<V, string>>;
  readonly choose: (data: D, context: HeadlineContext) => HeadlineChoice<V>;
}

/**
 * Declares a headline rule. An identity function with a type: it pins the variant names to
 * the keys, so a rule that returns a variant with no key does not compile.
 */
export function defineHeadline<D, V extends string>(rule: HeadlineRule<D, V>): HeadlineRule<D, V> {
  return rule;
}

export interface ResolvedHeadline<V extends string> {
  readonly variant: V;
  readonly key: string;
  readonly values: ICUValues;
}

/** The rule applied: the variant, its key and its arguments. Pure — the tests call this. */
export function resolveHeadline<D, V extends string>(
  rule: HeadlineRule<D, V>, data: D, locale: string,
): ResolvedHeadline<V> {
  const choice = rule.choose(data, { locale });
  return { variant: choice.variant, key: rule.keys[choice.variant], values: choice.values ?? {} };
}

// ── the header slot ──────────────────────────────────────────────────────────

const PageHeadlineSlot = createContext<HTMLElement | null>(null);

/** Provided by the router, with the header's headline node — the `PageAction` pattern. */
export const PageHeadlineSlotProvider = PageHeadlineSlot.Provider;

/**
 * Renders its children under the page title. The header is the ROUTER's and the data is the
 * screen's, so the screen portals the sentence up (as `PageAction` does its button); outside a
 * routed page — a test — it renders in place and is never lost.
 */
export function PageHeadline({ children }: { readonly children: ReactNode }): React.JSX.Element {
  const slot = useContext(PageHeadlineSlot);
  return slot === null ? <>{children}</> : createPortal(children, slot);
}

export interface HeadlineProps<D, V extends string> {
  readonly rule: HeadlineRule<D, V>;
  readonly data: D;
  /** Beside the sentence — the Illustrative tag, when the data is a fixture preview. */
  readonly tag?: ReactNode;
}

/** The sentence, resolved and placed under the page title. */
export function Headline<D, V extends string>({ rule, data, tag }: HeadlineProps<D, V>): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const resolved = resolveHeadline(rule, data, i18n.language);
  return (
    <PageHeadline>
      <p
        data-headline={resolved.variant}
        className="text-body text-ink-primary flex max-w-prose flex-wrap items-center gap-2 text-pretty"
      >
        <span>{t(resolved.key, resolved.values)}</span>
        {tag}
      </p>
    </PageHeadline>
  );
}
